import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Save, 
  X, 
  Eye, 
  Image as ImageIcon,
  Globe,
  Tag,
  Calendar,
  FileText,
  Search,
  Star,
  ArrowLeft,
  AlertCircle
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { ENV } from '../config/env';

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dataContext = useData();
  const { blogs } = dataContext;
  
  // Safely get functions with fallbacks
  const createFirebaseBlog = dataContext?.createFirebaseBlog || (async () => ({ success: false, error: 'Function not available' }));
  const updateFirebaseBlog = dataContext?.updateFirebaseBlog || (async () => ({ success: false, error: 'Function not available' }));
  
  const isEditMode = id && id !== 'new';

  const [formData, setFormData] = useState({
    // Basic Info
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Pet Care Tips',
    author: '',
    image: '',
    status: 'draft',
    featured: false,
    
    // SEO Fields
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    canonicalUrl: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    
    // Additional
    readTime: '5 min read',
    views: 0,
    date: new Date().toISOString().split('T')[0]
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const categories = ['Pet Care Tips', 'Dog Care', 'Cat Care', 'Training', 'Health', 'Grooming', 'Nutrition'];

  useEffect(() => {
    if (isEditMode && blogs) {
      const blog = blogs.find(b => b.id === id);
      if (blog) {
        setFormData(blog);
      }
    }
  }, [isEditMode, id, blogs]);

  // Auto-generate slug from title
  useEffect(() => {
    if (formData.title && (!isEditMode || !formData.slug)) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, isEditMode]);

  // Auto-fill SEO fields from basic fields
  useEffect(() => {
    if (formData.title && !formData.metaTitle) {
      setFormData(prev => ({ 
        ...prev, 
        metaTitle: formData.title,
        ogTitle: formData.title,
        twitterTitle: formData.title
      }));
    }
    if (formData.excerpt && !formData.metaDescription) {
      setFormData(prev => ({ 
        ...prev, 
        metaDescription: formData.excerpt,
        ogDescription: formData.excerpt,
        twitterDescription: formData.excerpt
      }));
    }
    if (formData.image && !formData.ogImage) {
      setFormData(prev => ({ 
        ...prev, 
        ogImage: formData.image,
        twitterImage: formData.image
      }));
    }
  }, [formData.title, formData.excerpt, formData.image]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.slug.trim()) newErrors.slug = 'Slug is required';
    if (!formData.excerpt.trim()) newErrors.excerpt = 'Excerpt is required';
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    if (!formData.author.trim()) newErrors.author = 'Author name is required';
    if (!formData.image.trim()) newErrors.image = 'Featured image URL is required';
    
    // SEO validations
    if (!formData.metaTitle.trim()) newErrors.metaTitle = 'Meta title is required for SEO';
    if (!formData.metaDescription.trim()) newErrors.metaDescription = 'Meta description is required for SEO';
    
    // Length validations
    if (formData.metaTitle.length > 60) newErrors.metaTitle = 'Meta title should be under 60 characters';
    if (formData.metaDescription.length > 160) newErrors.metaDescription = 'Meta description should be under 160 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      alert('Please fix the errors before saving');
      return;
    }

    setSaving(true);
    try {
      const blogData = {
        ...formData,
        updatedAt: new Date().toISOString()
      };

      let result;
      if (isEditMode) {
        result = await updateFirebaseBlog(id, blogData);
        if (result?.success) {
          alert('Blog post updated successfully!');
          navigate('/admin-marketing');
        } else {
          throw new Error(result?.error || 'Update failed');
        }
      } else {
        blogData.createdAt = new Date().toISOString();
        result = await createFirebaseBlog(blogData);
        if (result?.success) {
          alert('Blog post created successfully!');
          navigate('/admin-marketing');
        } else {
          throw new Error(result?.error || 'Create failed');
        }
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      alert(`Failed to save blog post: ${error.message}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setFormData(prev => ({ ...prev, status: 'published' }));
    // Submit will be triggered after state update
    setTimeout(() => {
      document.getElementById('blog-form').requestSubmit();
    }, 100);
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin-marketing"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Marketing
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isEditMode ? 'Update your blog post with SEO optimization' : 'Write and optimize your blog post for search engines'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {formData.status === 'published' && formData.slug && (
                <a
                  href={`/blog/${formData.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </a>
              )}
            </div>
          </div>
        </div>

        <form id="blog-form" onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[var(--pf-accent)]" />
              Basic Information
            </h2>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter blog post title..."
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.title}
                  </p>
                )}
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Slug <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-sm">/blog/</span>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent ${
                      errors.slug ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="url-friendly-slug"
                  />
                </div>
                {errors.slug && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.slug}
                  </p>
                )}
                <p className="text-gray-500 text-xs mt-1">Auto-generated from title, but you can customize it</p>
              </div>

              {/* Excerpt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Excerpt <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent ${
                    errors.excerpt ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Brief description of the blog post (shown in listings)..."
                />
                {errors.excerpt && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.excerpt}
                  </p>
                )}
                <p className="text-gray-500 text-xs mt-1">{formData.excerpt.length} characters</p>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  rows="15"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent font-mono text-sm ${
                    errors.content ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Write your blog content using HTML tags: <h2>, <h3>, <p>, <ul>, <ol>, <li>, <strong>, etc..."
                />
                {errors.content && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.content}
                  </p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Use HTML formatting. Supported tags: h2, h3, p, ul, ol, li, strong, em, a
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Author */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Author Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent ${
                      errors.author ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="John Doe"
                  />
                  {errors.author && <p className="text-red-500 text-xs mt-1">{errors.author}</p>}
                </div>

                {/* Read Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Read Time
                  </label>
                  <input
                    type="text"
                    name="readTime"
                    value={formData.readTime}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                    placeholder="5 min read"
                  />
                </div>
              </div>

              {/* Featured Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Featured Image URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent ${
                    errors.image ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://images.unsplash.com/photo-..."
                />
                {errors.image && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.image}
                  </p>
                )}
                {formData.image && (
                  <div className="mt-2">
                    <img src={formData.image} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                  </div>
                )}
                <p className="text-gray-500 text-xs mt-1">Recommended size: 1200x800px</p>
              </div>

              {/* Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Publish Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleChange}
                      className="w-4 h-4 text-[var(--pf-accent)] border-gray-300 rounded focus:ring-[var(--pf-accent)]"
                    />
                    <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500" />
                      Mark as Featured Post
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* SEO Optimization Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Search className="w-5 h-5 text-green-600" />
              SEO Optimization
            </h2>
            
            <div className="space-y-4">
              {/* Meta Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="metaTitle"
                  value={formData.metaTitle}
                  onChange={handleChange}
                  maxLength="60"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent ${
                    errors.metaTitle ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="SEO-optimized title for search engines"
                />
                <div className="flex justify-between text-xs mt-1">
                  {errors.metaTitle ? (
                    <p className="text-red-500">{errors.metaTitle}</p>
                  ) : (
                    <p className="text-gray-500">Shown in search results (50-60 chars recommended)</p>
                  )}
                  <span className={formData.metaTitle.length > 60 ? 'text-red-500' : 'text-gray-500'}>
                    {formData.metaTitle.length}/60
                  </span>
                </div>
              </div>

              {/* Meta Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleChange}
                  maxLength="160"
                  rows="3"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent ${
                    errors.metaDescription ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Brief description for search engine results"
                />
                <div className="flex justify-between text-xs mt-1">
                  {errors.metaDescription ? (
                    <p className="text-red-500">{errors.metaDescription}</p>
                  ) : (
                    <p className="text-gray-500">Shown in search results (150-160 chars recommended)</p>
                  )}
                  <span className={formData.metaDescription.length > 160 ? 'text-red-500' : 'text-gray-500'}>
                    {formData.metaDescription.length}/160
                  </span>
                </div>
              </div>

              {/* Meta Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Keywords
                </label>
                <input
                  type="text"
                  name="metaKeywords"
                  value={formData.metaKeywords}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                  placeholder="pet care, dog training, cat health (comma-separated)"
                />
                <p className="text-gray-500 text-xs mt-1">Separate keywords with commas</p>
              </div>

              {/* Canonical URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Canonical URL
                </label>
                <input
                  type="url"
                  name="canonicalUrl"
                  value={formData.canonicalUrl}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                  placeholder={`${ENV.APP.URL}/blog/your-post`}
                />
                <p className="text-gray-500 text-xs mt-1">Prevents duplicate content issues</p>
              </div>
            </div>
          </div>

          {/* Social Media Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              Social Media Preview
            </h2>
            
            <div className="space-y-6">
              {/* Open Graph (Facebook, LinkedIn) */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Open Graph (Facebook, LinkedIn)</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Title
                    </label>
                    <input
                      type="text"
                      name="ogTitle"
                      value={formData.ogTitle}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                      placeholder="Title for social media sharing"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Description
                    </label>
                    <textarea
                      name="ogDescription"
                      value={formData.ogDescription}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                      placeholder="Description for social media sharing"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      OG Image URL
                    </label>
                    <input
                      type="url"
                      name="ogImage"
                      value={formData.ogImage}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                      placeholder="https://..."
                    />
                    <p className="text-gray-500 text-xs mt-1">Recommended: 1200x630px</p>
                  </div>
                </div>
              </div>

              {/* Twitter Card */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Twitter Card</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Title
                    </label>
                    <input
                      type="text"
                      name="twitterTitle"
                      value={formData.twitterTitle}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                      placeholder="Title for Twitter sharing"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Description
                    </label>
                    <textarea
                      name="twitterDescription"
                      value={formData.twitterDescription}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                      placeholder="Description for Twitter sharing"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Twitter Image URL
                    </label>
                    <input
                      type="url"
                      name="twitterImage"
                      value={formData.twitterImage}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                      placeholder="https://..."
                    />
                    <p className="text-gray-500 text-xs mt-1">Recommended: 1200x600px</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Post Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin-marketing')}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold transition-colors"
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Draft'}
                </button>
                
                {formData.status === 'draft' && (
                  <button
                    type="button"
                    onClick={handlePublish}
                    disabled={saving}
                    className="pf-btn-primary px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <Globe className="w-4 h-4" />
                    Publish Now
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogEditor;

